/* Geschenkefinder — baut aus der Auswahl eine gefilterte Kategorie-URL.
   Bewusst ohne Theme-Module: der Baustein braucht nichts vom Warenkorb oder
   Produktformular, und so lässt er sich auf jeder Seite einsetzen. */
(() => {
  const START = "[data-gfinder-los]";

  /** Eine Zeile = eine Gruppe; je Gruppe ist höchstens eine Option aktiv. */
  function waehlen(knopf) {
    const gruppe = knopf.closest(".gfinder__optionen");
    const war = knopf.getAttribute("aria-checked") === "true";
    gruppe.querySelectorAll(".gfinder__option").forEach((b) => b.setAttribute("aria-checked", "false"));
    // Nochmal auf dieselbe Option = Auswahl aufheben. Sonst käme man aus einer
    // versehentlichen Wahl nicht mehr heraus, ohne die Seite neu zu laden.
    knopf.setAttribute("aria-checked", war ? "false" : "true");
  }

  /** Die Zeile, deren Auswahl den Pfad bestimmt (heute: „Alter"). */
  function kategorieZeile(wurzel) {
    return wurzel.querySelector('.gfinder__zeile[data-typ="kategorie"]');
  }

  /** Liest einen Preis-Regler aus. Gibt null zurueck, solange beide Griffe
      unbewegt an ihren Enden stehen — dann hat der Kunde keinen Preis gewaehlt,
      und wir haengen auch keinen Filter an. Das ist wichtig: sonst zaehlte jede
      Suche als "Preis gewaehlt" und die Nachfrage nach der Altersstufe kaeme
      auch dann, wenn niemand am Regler war. */
  function reglerLesen(zeile) {
    const box = zeile.querySelector("[data-min][data-max]");
    if (!box) return null;
    const min = Number(box.dataset.min);
    const max = Number(box.dataset.max);
    const von = Number(box.querySelector("[data-gfinder-von]")?.value ?? min);
    const bis = Number(box.querySelector("[data-gfinder-bis]")?.value ?? max);
    if (von <= min && bis >= max) return null;
    return { von, bis, min, max };
  }

  const euro = (n) => `${n} €`;

  /** Beschriftung unter dem Regler. Der obere Griff ganz rechts heisst "offen
      nach oben" — sonst wuerden die Artikel oberhalb der Reglergrenze stillschweigend
      verschwinden, obwohl der Kunde gar keine Obergrenze gesetzt hat. */
  function reglerBeschriften(zeile) {
    const box = zeile.querySelector("[data-min][data-max]");
    const ziel = zeile.querySelector("[data-gfinder-reglerwert]");
    if (!box || !ziel) return;
    const min = Number(box.dataset.min);
    const max = Number(box.dataset.max);
    const vonEl = box.querySelector("[data-gfinder-von]");
    const bisEl = box.querySelector("[data-gfinder-bis]");
    let von = Number(vonEl.value);
    let bis = Number(bisEl.value);

    // Die beiden Regler liegen uebereinander; ohne Klemmung koennte der untere
    // Griff den oberen ueberholen und die Spanne waere verdreht.
    if (von > bis) { if (document.activeElement === vonEl) { von = bis; vonEl.value = von; } else { bis = von; bisEl.value = bis; } }

    const spanne = max - min || 1;
    const balken = box.querySelector(".price-facet__range-inputs");
    if (balken) {
      balken.style.setProperty("--range-min", `${((von - min) / spanne) * 100}%`);
      balken.style.setProperty("--range-max", `${((bis - min) / spanne) * 100}%`);
      // Steht der untere Griff weit rechts, liegt er unter dem oberen und waere
      // nicht mehr greifbar. Dann kommt er nach vorn.
      balken.style.setProperty("--range-min-z-index", von > min + spanne / 2 ? "1" : "0");
    }

    const offen = bis >= max;
    if (von <= min && offen) ziel.textContent = "Alle Preise";
    else if (offen) ziel.textContent = `ab ${euro(von)}`;
    else if (von <= min) ziel.textContent = `bis ${euro(bis)}`;
    else ziel.textContent = `${euro(von)} – ${euro(bis)}`;
  }

  function zielUrl(wurzel) {
    const basis = wurzel.dataset.basis || "all";
    // Der Pfad wird ERST AM ENDE gebaut. Vorher standen Kategorie und
    // Schlagwort beide direkt in `pfad` — die zuletzt gelesene Zeile hat die
    // andere ueberschrieben, und genau deshalb ging „Anlass UND Alter“ nicht.
    let kategorie = "";
    let schlagwort = "";
    const params = new URLSearchParams();
    let etwasGewaehlt = false;

    wurzel.querySelectorAll(".gfinder__zeile").forEach((zeile) => {
      // Der Regler hat keine Chips - er wird vor der Chip-Abfrage behandelt.
      if (zeile.dataset.typ === "preisregler") {
        const r = reglerLesen(zeile);
        if (!r) return;
        etwasGewaehlt = true;
        if (r.von > r.min) params.append("filter.v.price.gte", r.von);
        if (r.bis < r.max) params.append("filter.v.price.lte", r.bis);
        return;
      }

      const aktiv = zeile.querySelector('.gfinder__option[aria-checked="true"]');
      if (!aktiv) return;
      etwasGewaehlt = true;
      const wert = aktiv.dataset.wert || "";

      switch (zeile.dataset.typ) {
        case "kategorie":
          kategorie = wert;
          break;

        case "tag":
          // Kollektion UND Schlagwort in EINEM Pfad: /collections/<handle>/<tag>.
          // Am 22.08. auf der Storefront gemessen: das ist die einzige Form, die
          // Shopify zusammen mit der Kollektion auswertet. `filter.p.tag=…` wird
          // hier STILL ignoriert (in Search & Discovery ist kein Schlagwort-Filter
          // eingerichtet — die Seite liefert dann unveraendert die ganze Kollektion).
          schlagwort = wert;
          break;

        case "metafeld": {
          const feld = (zeile.dataset.feld || "").trim();
          // namespace.key → filter.p.m.<namespace>.<key>
          if (feld.includes(".")) params.append(`filter.p.m.${feld}`, wert);
          break;
        }

        case "preis": {
          // "von-bis", offenes Ende erlaubt: "50-" oder "-20"
          const [von, bis] = wert.split("-").map((x) => x.trim());
          if (von) params.append("filter.v.price.gte", von);
          if (bis) params.append("filter.v.price.lte", bis);
          break;
        }
      }
    });

    if (!etwasGewaehlt) return { fehler: "leer" };

    // Shopify liefert Filter nur für Kollektionen mit höchstens 5.000 Produkten.
    // Die Basis-Kollektion („all") liegt im Shop weit darüber (7.589 Produkte),
    // /collections/geschenke ebenso (5.626). Dort werden filter.*-Parameter
    // STILL ignoriert: Die Seite lädt, zeigt aber den kompletten Katalog.
    // Genau das war der Fehler — deshalb wird ohne Kategorie-Auswahl keine
    // gefilterte URL mehr gebaut, sondern nachgefragt.
    if (params.toString() && !kategorie && kategorieZeile(wurzel)) {
      return { fehler: "kategorie" };
    }

    // Ein Schlagwort allein ist unkritisch: /collections/all/<tag> funktioniert
    // (geprueft). Nur die filter.*-Parameter brauchen eine kleine Kollektion.
    let pfad = `/collections/${kategorie || basis}`;
    if (schlagwort) pfad += `/${encodeURIComponent(schlagwort)}`;

    const q = params.toString();
    return { url: q ? `${pfad}?${q}` : pfad };
  }

  /** Zeigt den Hinweis und sagt dabei, WAS fehlt. */
  function melden(wurzel, grund) {
    const hinweis = wurzel.querySelector("[data-gfinder-hinweis]");
    if (!hinweis) return;

    // Den im Theme-Editor gepflegten Text einmalig merken, damit er nicht
    // verloren geht, wenn wir zwischendurch eine genauere Meldung einsetzen.
    if (hinweis.dataset.gfinderStandardtext === undefined) {
      hinweis.dataset.gfinderStandardtext = hinweis.textContent.trim();
    }

    if (grund === "kategorie") {
      const name = kategorieZeile(wurzel)?.querySelector(".gfinder__gruppe")?.textContent.trim();
      hinweis.textContent = name
        ? `Bitte wähle auch „${name}“ aus — sonst können wir deine Auswahl nicht berücksichtigen.`
        : "Bitte wähle auch eine Kategorie aus — sonst können wir deine Auswahl nicht berücksichtigen.";
    } else {
      hinweis.textContent = hinweis.dataset.gfinderStandardtext;
    }

    hinweis.removeAttribute("hidden");
  }

  document.addEventListener("click", (e) => {
    const opt = e.target.closest(".gfinder__option");
    if (opt) {
      waehlen(opt);
      const w = opt.closest(".gfinder");
      w?.querySelector("[data-gfinder-hinweis]")?.setAttribute("hidden", "");
      return;
    }

    const los = e.target.closest(START);
    if (!los) return;
    const wurzel = los.closest(".gfinder");
    if (!wurzel) return;

    const ziel = zielUrl(wurzel);
    if (ziel.fehler) {
      // Nichts oder zu wenig gewählt: nicht kommentarlos auf eine Seite
      // schicken, die die Auswahl gar nicht anwenden kann.
      melden(wurzel, ziel.fehler);
      return;
    }
    window.location.href = ziel.url;
  });

  // Regler: waehrend des Ziehens Balken und Beschriftung nachfuehren. Der
  // Hinweis wird zurueckgesetzt, damit eine alte Meldung nicht stehen bleibt.
  document.addEventListener("input", (e) => {
    const feld = e.target.closest?.("[data-gfinder-von], [data-gfinder-bis]");
    if (!feld) return;
    const zeile = feld.closest(".gfinder__zeile");
    if (!zeile) return;
    reglerBeschriften(zeile);
    zeile.closest(".gfinder")?.querySelector("[data-gfinder-hinweis]")?.setAttribute("hidden", "");
  });

  // Anfangszustand setzen, sobald das Markup steht.
  const reglerStarten = () => document.querySelectorAll('.gfinder__zeile[data-typ="preisregler"]').forEach(reglerBeschriften);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", reglerStarten);
  else reglerStarten();

  // Tastatur: Pfeiltasten innerhalb einer Gruppe, wie bei Radiobuttons üblich.
  document.addEventListener("keydown", (e) => {
    if (!["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"].includes(e.key)) return;
    const opt = e.target.closest?.(".gfinder__option");
    if (!opt) return;
    e.preventDefault();
    // (Die range-Felder tragen diese Klasse nicht - sie behalten ihre eigene
    //  Pfeiltasten-Bedienung, die der Browser mitbringt.)
    const alle = [...opt.closest(".gfinder__optionen").querySelectorAll(".gfinder__option")];
    const i = alle.indexOf(opt);
    const vor = e.key === "ArrowRight" || e.key === "ArrowDown";
    const ziel = alle[(i + (vor ? 1 : -1) + alle.length) % alle.length];
    ziel.focus();
    waehlen(ziel);
  });
})();
